import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import {
  PiApi, CustomerApi, ProductApi, WarrantyPresetApi, SettingsApi, MachineConfigApi,
} from '@/api';
import { formatMoneyWithSymbol, formatMoney } from '@/lib/format';
import type { PiLine, Product, MachineConfig, MachineFamily } from '@/types';
import type { Currency } from '@/lib/format';
import { ChevronLeft, ChevronRight, Plus, Trash2, ArrowUp, ArrowDown, UserPlus } from 'lucide-react';
import { CustomerFormModal } from '@/components/customers/CustomerFormModal';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

const STEPS = ['Customer', 'Lines', 'Tax', 'Terms', 'Review'];

const FAMILY_LABELS: Record<MachineFamily, string> = {
  nova: 'Nova', nano: 'Nano', orbiton: 'Orbiton', roll_format: 'Roll Format',
};
const HEAD_COUNT_FAMILIES: MachineFamily[] = ['nova', 'nano'];

// Static per-config specs, same set the PDF prints under the machine name —
// scoped to whichever fields the selected machine's family actually has set.
// Heads/Flash Heater/Printhead Type are excluded here since those are already
// shown as their own editable per-line fields elsewhere in the wizard.
function machineSpecLines(cfg: MachineConfig): string[] {
  const colourLine =
    cfg.colour_count != null && cfg.colour_config
      ? `${cfg.colour_count} Colour (${cfg.colour_config})`
      : cfg.colour_config || (cfg.colour_count != null ? `${cfg.colour_count} Colour` : null);
  return [
    colourLine,
    cfg.pallet_count != null ? `${cfg.pallet_count} Pallets` : null,
    cfg.screen_station != null ? `${cfg.screen_station} Screen Stations` : null,
    cfg.pallet_size ? `Pallet Size: ${cfg.pallet_size}` : null,
    cfg.print_area ? `Print Area: ${cfg.print_area}` : null,
    cfg.single_step ? `Single Step: ${cfg.single_step}` : null,
    cfg.standard_accessories,
  ].filter((x): x is string => !!x);
}

type WizardLine = PiLine & { _temp_id?: string; _source?: 'product' | 'machine' };

function lineToPayload(l: WizardLine) {
  return {
    product_id: l._source === 'machine' ? undefined : (l.product_id || undefined),
    machine_config_id: l._source === 'machine' ? (l.machine_config_id || undefined) : undefined,
    quantity: l.quantity,
    unit_price: l.unit_price,
    discount_pct: l.discount_pct,
    discount_amount: l.discount_amount,
    selected_head_count: l._source === 'machine' ? (l.selected_head_count ?? undefined) : undefined,
    flash_heater_value: l._source === 'machine' ? (l.flash_heater_value || undefined) : undefined,
    printhead_type: l._source === 'machine' ? (l.printhead_type || undefined) : undefined,
  };
}

export default function PiCreate() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [step, setStep] = useState(0);
  const [customerId, setCustomerId] = useState('');
  const [piDate, setPiDate] = useState(new Date().toISOString().split('T')[0]);
  const [currency, setCurrency] = useState<Currency>('INR');
  const [validityDays, setValidityDays] = useState(30);
  const [advancePct, setAdvancePct] = useState<number | ''>('');
  const [buyerRefNo, setBuyerRefNo] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [gstRateOverride, setGstRateOverride] = useState<number | ''>('');
  const [taxOnExport, setTaxOnExport] = useState(false);
  const [gstTypeOverride, setGstTypeOverride] = useState<'intra' | 'inter' | ''>('');
  const [lines, setLines] = useState<WizardLine[]>([]);
  const [paymentPresetId, setPaymentPresetId] = useState('');
  const [warrantyPresetId, setWarrantyPresetId] = useState('');
  const [warrantyMonthsOverride, setWarrantyMonthsOverride] = useState<number | ''>('');
  const [leadTimeWeeks, setLeadTimeWeeks] = useState(0);
  const [notes, setNotes] = useState('');
  const [discountMode, setDiscountMode] = useState<Record<string, 'pct' | 'amt'>>({});
  const [showDiscountOnPdf, setShowDiscountOnPdf] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showNewCustomer, setShowNewCustomer] = useState(false);

  // Load existing PI if editing
  const existingPiQuery = useQuery({
    queryKey: ['pi', id],
    queryFn: () => PiApi.get(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingPiQuery.data) {
      const pi = existingPiQuery.data;
      setCustomerId(pi.customer_id);
      // pi.pi_date comes back as a full ISO timestamp; the field only ever
      // holds YYYY-MM-DD (same normalization as the new-PI default above).
      setPiDate((pi.pi_date || '').split('T')[0]);
      setCurrency(pi.currency);
      setValidityDays(pi.validity_days);
      setAdvancePct(pi.advance_pct != null ? Number(pi.advance_pct) : '');
      setBuyerRefNo(pi.buyer_ref_no ?? '');
      setDeliveryTerms(pi.delivery_terms ?? '');
      setGstRateOverride(pi.gst_rate_override != null ? Number(pi.gst_rate_override) : '');
      setTaxOnExport(pi.tax_on_export ?? false);
      setGstTypeOverride(pi.gst_type_override ?? '');
      // Existing line items don't carry a _temp_id — synthesise one so the move/remove
      // handlers (which key off _temp_id) work correctly. _source is derived from
      // which id the line actually carries.
      const mappedLines: WizardLine[] = pi.lines.map((l, idx) => ({
        ...l,
        _temp_id: `existing_${idx}`,
        _source: l.machine_config_id ? 'machine' : 'product',
      }));
      setLines(mappedLines);
      const initialDiscountMode: Record<string, 'pct' | 'amt'> = {};
      mappedLines.forEach((l) => {
        initialDiscountMode[l._temp_id!] = (l.discount_amount ?? 0) > 0 ? 'amt' : 'pct';
      });
      setDiscountMode(initialDiscountMode);
      setPaymentPresetId(pi.payment_preset_id || '');
      setWarrantyPresetId(pi.warranty_preset_id || '');
      setWarrantyMonthsOverride(pi.warranty_months_override ?? '');
      setLeadTimeWeeks(pi.lead_time_weeks);
      setNotes(pi.notes);
      setShowDiscountOnPdf(pi.show_discount_on_pdf);
    }
  }, [existingPiQuery.data]);

  // Data queries
  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: () => CustomerApi.list({ limit: 1000 }),
  });

  const customersList = customersQuery.data?.data ?? customersQuery.data ?? [];
  const selectedCustomer = customersList.find((c: any) => c.id === customerId);

  useEffect(() => {
    if (selectedCustomer && !isEdit) {
      setCurrency(selectedCustomer.currency_default);
    }
  }, [selectedCustomer, isEdit]);

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => SettingsApi.get(),
  });

  useEffect(() => {
    if (settingsQuery.data && !isEdit) {
      setValidityDays(settingsQuery.data.default_pi_validity_days);
    }
  }, [settingsQuery.data, isEdit]);

  // Auto-derive Intra/Inter-State from the selected customer's state vs RDX's
  // own (Tamil Nadu) — reacts every time the customer changes, same pattern
  // as the currency auto-fill above. Skipped when editing a PI that already
  // has an explicit saved override, so we don't clobber a manual correction.
  useEffect(() => {
    if (!selectedCustomer || !settingsQuery.data) return;
    if (isEdit && existingPiQuery.data?.gst_type_override) return;
    const same =
      (selectedCustomer.state ?? '').trim().toLowerCase() ===
      (settingsQuery.data.company_state ?? '').trim().toLowerCase();
    setGstTypeOverride(same ? 'intra' : 'inter');
  }, [selectedCustomer, settingsQuery.data, isEdit, existingPiQuery.data]);

  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => ProductApi.list({ limit: 1000 }),
  });
  const productsList: any[] = productsQuery.data?.data ?? productsQuery.data ?? [];

  const machineConfigsQuery = useQuery({
    queryKey: ['machineConfigs'],
    queryFn: () => MachineConfigApi.list(),
  });
  const machineConfigsList: MachineConfig[] = machineConfigsQuery.data?.data ?? [];

  const warrantyPresetsQuery = useQuery({
    queryKey: ['warrantyPresets'],
    queryFn: () => WarrantyPresetApi.list(),
  });

  const previewQuery = useQuery({
    queryKey: ['piPreview', lines, customerId, currency, gstRateOverride, taxOnExport, gstTypeOverride],
    queryFn: async () => {
      if (!customerId || !lines.length) return null;
      const body = {
        customer_id: customerId,
        currency,
        lines: lines.map(lineToPayload),
        gst_rate_override: gstRateOverride === '' ? null : gstRateOverride,
        tax_on_export: taxOnExport,
        gst_type_override: currency === 'INR' ? (gstTypeOverride || null) : null,
      };
      return PiApi.preview(body);
    },
    // Only run the preview when EVERY line has a source, qty > 0, and unit price > 0.
    enabled:
      !!customerId &&
      lines.length > 0 &&
      lines.every((l) => !!(l.product_id || l.machine_config_id) && Number(l.quantity) > 0 && Number(l.unit_price) > 0),
    retry: false,
  });

  // Mutations
  const createMutation = useMutation<string, Error, void>({
    mutationFn: async () => {
      const body = {
        customer_id: customerId,
        pi_date: piDate,
        currency,
        validity_days: validityDays,
        advance_pct: advancePct === '' ? null : advancePct,
        buyer_ref_no: buyerRefNo.trim() || null,
        delivery_terms: deliveryTerms || null,
        gst_rate_override: gstRateOverride === '' ? null : gstRateOverride,
        tax_on_export: taxOnExport,
        gst_type_override: currency === 'INR' ? (gstTypeOverride || null) : null,
        lines: lines.map(lineToPayload),
        payment_preset_id: paymentPresetId || null,
        warranty_preset_id: warrantyPresetId || null,
        warranty_months_override: warrantyMonthsOverride === '' ? null : warrantyMonthsOverride,
        lead_time_weeks: leadTimeWeeks,
        notes,
        show_discount_on_pdf: showDiscountOnPdf,
      };
      const res = await PiApi.create(body);
      return res.id;
    },
    onSuccess: (piId) => {
      push({ tone: 'success', title: 'PI saved successfully' });
      navigate(`/pis/${piId}`);
      queryClient.invalidateQueries({ queryKey: ['piList'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to save PI', body: err.message });
    },
  });

  const updateMutation = useMutation<string | undefined, Error, void>({
    mutationFn: async () => {
      const body = {
        customer_id: customerId,
        pi_date: piDate,
        currency,
        validity_days: validityDays,
        advance_pct: advancePct === '' ? null : advancePct,
        buyer_ref_no: buyerRefNo.trim() || null,
        delivery_terms: deliveryTerms || null,
        gst_rate_override: gstRateOverride === '' ? null : gstRateOverride,
        tax_on_export: taxOnExport,
        gst_type_override: currency === 'INR' ? (gstTypeOverride || null) : null,
        lines: lines.map(lineToPayload),
        payment_preset_id: paymentPresetId || null,
        warranty_preset_id: warrantyPresetId || null,
        warranty_months_override: warrantyMonthsOverride === '' ? null : warrantyMonthsOverride,
        lead_time_weeks: leadTimeWeeks,
        notes,
        show_discount_on_pdf: showDiscountOnPdf,
      };
      await PiApi.update(id!, body);
      return id;
    },
    onSuccess: () => {
      push({ tone: 'success', title: 'PI saved successfully' });
      navigate(`/pis/${id}`);
      queryClient.invalidateQueries({ queryKey: ['piList'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to save PI', body: err.message });
    },
  });

  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (s === 0) {
      if (!customerId) newErrors.customerId = 'Customer is required';
      if (!piDate) newErrors.piDate = 'PI date is required';
    }
    if (s === 1) {
      if (lines.length === 0) newErrors.lines = 'At least one line is required';
      lines.forEach((line, idx) => {
        if (line._source === 'machine') {
          if (!line.machine_config_id) newErrors[`line_${idx}_product`] = 'Machine configuration required';
          const cfg = machineConfigsList.find((m) => m.id === line.machine_config_id);
          if (cfg) {
            if (HEAD_COUNT_FAMILIES.includes(cfg.family) && !line.selected_head_count) {
              newErrors[`line_${idx}_machine`] = 'Head count is required';
            }
            if (cfg.family === 'roll_format' && cfg.printhead_types?.length && !line.printhead_type) {
              newErrors[`line_${idx}_printhead`] = 'Printhead type is required';
            }
            if (cfg.family === 'orbiton') {
              const fh = line.flash_heater_value?.trim();
              if (!fh) {
                newErrors[`line_${idx}_machine`] = 'Flash Heater value is required';
              } else if (cfg.colour_count != null && (Number(fh) < 1 || Number(fh) > cfg.colour_count)) {
                newErrors[`line_${idx}_machine`] = `Flash Heater must be between 1 and ${cfg.colour_count}`;
              }
            }
          }
        } else if (!line.product_id) {
          newErrors[`line_${idx}_product`] = 'Product required';
        }
        if (!line.quantity || line.quantity <= 0) newErrors[`line_${idx}_qty`] = 'Qty must be > 0';
        if (line.quantity > 99999) newErrors[`line_${idx}_qty`] = 'Qty too large';
        if (!line.unit_price || line.unit_price <= 0) newErrors[`line_${idx}_price`] = 'Price must be > 0';
        const range = getCatalogPriceRange(line);
        if (range.min > 0 && line.unit_price < range.min)
          newErrors[`line_${idx}_price`] = `Price below minimum (${formatMoney(range.min, currency)})`;
        if (range.max > 0 && line.unit_price > range.max)
          newErrors[`line_${idx}_price`] = `Price above maximum (${formatMoney(range.max, currency)})`;
        const discPct = Number(line.discount_pct) || 0;
        const discAmt = Number(line.discount_amount) || 0;
        if (discPct > 0 && discAmt > 0)
          newErrors[`line_${idx}_discount`] = 'Use either % or amount, not both';
        if (discPct > 100)
          newErrors[`line_${idx}_discount`] = 'Discount % cannot exceed 100';
        if (discAmt > 0 && discAmt >= line.unit_price * line.quantity)
          newErrors[`line_${idx}_discount`] = 'Discount cannot exceed line total';
      });
    }
    if (s === 3) {
      if (leadTimeWeeks < 0 || leadTimeWeeks > 104)
        newErrors.leadTimeWeeks = 'Lead time must be 0-104 weeks';
      if (validityDays <= 0 || validityDays > 365) newErrors.validityDays = 'Validity must be 1-365 days';
      if (advancePct !== '' && (advancePct < 0 || advancePct > 100))
        newErrors.advancePct = 'Advance % must be 0-100';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
  };

  const handlePrev = () => {
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleAddLine = () => {
    setLines((ls) => [
      ...ls,
      {
        _temp_id: `temp_${Date.now()}`,
        _source: 'machine',
        product_id: '',
        quantity: 1,
        unit_price: 0,
        discount_pct: 0,
        discount_amount: 0,
      },
    ]);
  };

  const handleSetLineSource = (tempId: string | undefined, source: 'product' | 'machine') => {
    setLines((ls) =>
      ls.map((l) =>
        l._temp_id === tempId
          ? {
              ...l,
              _source: source,
              product_id: '',
              machine_config_id: undefined,
              selected_head_count: undefined,
              flash_heater_value: '',
              printhead_type: undefined,
            }
          : l
      )
    );
  };

  // Picking a machine config seeds the head-count field: Nova/Nano default to
  // the first entry in their confirmed head_options list (Orbiton/Roll Format
  // don't use head count at all). Printhead type (Roll Format only) always
  // resets — the new config's available types may differ from the previous one's.
  const handleSelectMachineConfig = (tempId: string | undefined, configId: string) => {
    const cfg = machineConfigsList.find((m) => m.id === configId);
    setLines((ls) =>
      ls.map((l) => {
        if (l._temp_id !== tempId) return l;
        if (!cfg) return { ...l, machine_config_id: configId };
        const firstOption = cfg.head_options?.length ? cfg.head_options[0] : undefined;
        return {
          ...l,
          machine_config_id: configId,
          selected_head_count: firstOption,
          flash_heater_value: '',
          printhead_type: undefined,
        };
      })
    );
  };

  const handleRemoveLine = (tempId: string | undefined) => {
    setLines((ls) => ls.filter((l) => l._temp_id !== tempId));
  };

  const handleUpdateLine = (tempId: string | undefined, field: string, value: any) => {
    setLines((ls) =>
      ls.map((l) => (l._temp_id === tempId ? { ...l, [field]: value } : l))
    );
  };

  const handleMoveLine = (tempId: string | undefined, direction: 'up' | 'down') => {
    const idx = lines.findIndex((l) => l._temp_id === tempId);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === lines.length - 1)) {
      return;
    }
    const newLines = [...lines];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newLines[idx], newLines[swapIdx]] = [newLines[swapIdx], newLines[idx]];
    setLines(newLines);
  };

  const getLineCatalogItem = (line: WizardLine): (Product | MachineConfig) | undefined => {
    if (line._source === 'machine' || line.machine_config_id) {
      return machineConfigsList.find((m) => m.id === line.machine_config_id);
    }
    return productsList.find((p) => p.id === line.product_id);
  };

  const getCatalogPriceRange = (line: WizardLine) => {
    const item = getLineCatalogItem(line);
    if (!item) return { min: 0, max: 0, mid: 0 };
    const minKey = `min_price_${currency.toLowerCase()}` as keyof Product;
    const maxKey = `max_price_${currency.toLowerCase()}` as keyof Product;
    const min = Number((item as any)[minKey]) || 0;
    const max = Number((item as any)[maxKey]) || 0;
    return { min, max, mid: (min + max) / 2 };
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title={isEdit ? 'Edit PI' : 'Create New PI'}
        subtitle={isEdit ? 'Modify draft proforma invoice' : 'Fill in the details to create a new PI'}
      />

      {/* Stepper */}
      <div className="flex items-center justify-between">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center flex-1">
            <button
              onClick={() => setStep(i)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition ${
                i === step
                  ? 'bg-rdx text-white'
                  : i < step
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-500'
              }`}
            >
              {i < step ? '✓' : i + 1}
            </button>
            <div className="text-xs font-medium text-muted ml-2 hidden sm:block">{s}</div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-4 ${i < step ? 'bg-emerald-100' : 'bg-slate-100'}`} />
            )}
          </div>
        ))}
      </div>

      <div className="card card-pad min-h-96">
        {/* Step 0: Customer */}
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <label className="label">Customer *</label>
              <div className="flex gap-2 mt-1">
                <SearchableSelect
                  className={`flex-1 ${errors.customerId ? 'input-error' : ''}`}
                  value={customerId}
                  onChange={setCustomerId}
                  placeholder="Select customer..."
                  options={customersList.map((c: any) => ({
                    value: c.id,
                    label: c.name,
                    sublabel: c.customer_type === 'export' ? `Export - ${c.country}` : c.gstin ?? undefined,
                  }))}
                />
                <button
                  type="button"
                  className="btn-secondary gap-1.5 whitespace-nowrap"
                  onClick={() => setShowNewCustomer(true)}
                  title="Create a new customer without leaving this PI"
                >
                  <UserPlus size={14} /> New customer
                </button>
              </div>
              {errors.customerId && <div className="text-xs text-rdx mt-1">{errors.customerId}</div>}
              {selectedCustomer && (
                <div className="text-xs text-muted mt-1.5 leading-relaxed">
                  {selectedCustomer.billing_address}
                  {selectedCustomer.gstin && <> &nbsp;•&nbsp; GSTIN {selectedCustomer.gstin}</>}
                </div>
              )}
            </div>

            <div>
              <label className="label">PI Date *</label>
              <input
                type="date"
                className={`input mt-1 ${errors.piDate ? 'input-error' : ''}`}
                value={piDate}
                onChange={(e) => setPiDate(e.target.value)}
              />
              {errors.piDate && <div className="text-xs text-rdx mt-1">{errors.piDate}</div>}
            </div>

            <div>
              <label className="label">Buyer's Ref. / Order No. <span className="text-muted font-normal normal-case">(optional)</span></label>
              <input
                type="text"
                className="input mt-1"
                value={buyerRefNo}
                onChange={(e) => setBuyerRefNo(e.target.value)}
                placeholder="Customer's PO or order number"
              />
            </div>

            <div>
              <label className="label">Currency *</label>
              <select
                className="input mt-1"
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value as any);
                  if (lines.length > 0) {
                    setLines((ls) => ls.map((l) => ({ ...l, unit_price: 0 })));
                    push({ tone: 'info', title: 'Currency changed — please re-enter unit prices' });
                  }
                }}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 1: Lines */}
        {step === 1 && (
          <div className="space-y-4">
            {errors.lines && <div className="text-xs text-rdx bg-rdx-50 p-2 rounded">{errors.lines}</div>}

            {lines.map((line, idx) => {
              const range = getCatalogPriceRange(line);
              const dMode = discountMode[line._temp_id!] ?? 'pct';
              const previewLine = previewQuery.data?.lines?.[idx];
              const selectedConfig = machineConfigsList.find((m) => m.id === line.machine_config_id);
              return (
                <div key={line._temp_id} className="p-3 border border-line rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex rounded overflow-hidden border border-line text-[10px] font-medium">
                      <button
                        type="button"
                        onClick={() => handleSetLineSource(line._temp_id, 'machine')}
                        className={`px-2 py-0.5 ${line._source === 'machine' ? 'bg-ink text-white' : 'text-muted hover:bg-slate-50'}`}
                      >
                        Machine
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSetLineSource(line._temp_id, 'product')}
                        className={`px-2 py-0.5 ${line._source !== 'machine' ? 'bg-ink text-white' : 'text-muted hover:bg-slate-50'}`}
                      >
                        Product
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-[1fr_80px_140px_140px_120px_auto] gap-2 items-start">
                    {/* Product or Machine config */}
                    <div className="col-span-2 lg:col-span-1">
                      <label className="label text-xs">{line._source === 'machine' ? 'Machine' : 'Product'}</label>
                      {line._source === 'machine' ? (
                        <SearchableSelect
                          className={`mt-0.5 ${errors[`line_${idx}_product`] ? 'input-error' : ''}`}
                          value={line.machine_config_id ?? ''}
                          onChange={(val) => handleSelectMachineConfig(line._temp_id, val)}
                          placeholder="Select machine..."
                          options={machineConfigsList.map((m) => ({
                            value: m.id,
                            label: m.name,
                            sublabel: m.printhead_types?.length
                              ? `${FAMILY_LABELS[m.family]} · ${m.printhead_types.join(' / ')}`
                              : FAMILY_LABELS[m.family],
                          }))}
                        />
                      ) : (
                        <SearchableSelect
                          className={`mt-0.5 ${errors[`line_${idx}_product`] ? 'input-error' : ''}`}
                          value={line.product_id ?? ''}
                          onChange={(val) => handleUpdateLine(line._temp_id, 'product_id', val)}
                          placeholder="Select..."
                          options={productsList.map((p) => ({
                            value: p.id,
                            label: p.name,
                            sublabel: p.sku ?? undefined,
                          }))}
                        />
                      )}
                    </div>
                    {/* Qty */}
                    <div>
                      <label className="label text-xs">Qty</label>
                      <input
                        type="number"
                        className={`input mt-0.5 text-sm ${errors[`line_${idx}_qty`] ? 'input-error' : ''}`}
                        value={line.quantity || ''}
                        onChange={(e) => handleUpdateLine(line._temp_id, 'quantity', e.target.value === '' ? 0 : Number(e.target.value))}
                        onFocus={(e) => e.target.select()}
                        min={1}
                      />
                    </div>
                    {/* Unit Price */}
                    <div>
                      <label className="label text-xs">Unit Price</label>
                      <div className="relative mt-0.5">
                        <input
                          type="number"
                          className={`input text-sm w-full ${errors[`line_${idx}_price`] ? 'input-error' : ''}`}
                          value={line.unit_price || ''}
                          onChange={(e) => handleUpdateLine(line._temp_id, 'unit_price', e.target.value === '' ? 0 : Number(e.target.value))}
                          onFocus={(e) => e.target.select()}
                        />
                        {range.mid > 0 && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-xs"
                            onClick={() => handleUpdateLine(line._temp_id, 'unit_price', range.mid)}
                            title={`Set midpoint: ${formatMoney(range.mid, currency)}`}
                          >
                            ◐
                          </button>
                        )}
                      </div>
                      {range.min > 0 && (
                        <div className={`text-xs mt-1 ${
                          line.unit_price > 0 && line.unit_price < range.min ? 'text-rdx font-medium'
                          : line.unit_price > range.max ? 'text-rdx font-medium'
                          : 'text-muted'
                        }`}>
                          {line.unit_price > 0 && line.unit_price < range.min
                            ? `Below minimum — must be at least ${formatMoney(range.min, currency)}`
                            : line.unit_price > range.max
                            ? `Exceeds maximum — must be at most ${formatMoney(range.max, currency)}`
                            : `${formatMoney(range.min, currency)} - ${formatMoney(range.max, currency)}`}
                        </div>
                      )}
                    </div>
                    {/* Discount — % or flat amount, toggled */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="label text-xs">Discount</label>
                        <div className="flex rounded overflow-hidden border border-line text-[10px] font-medium">
                          <button
                            type="button"
                            onClick={() => {
                              setDiscountMode((m) => ({ ...m, [line._temp_id!]: 'pct' }));
                              handleUpdateLine(line._temp_id, 'discount_amount', 0);
                            }}
                            className={`px-1.5 py-0.5 ${dMode === 'pct' ? 'bg-ink text-white' : 'text-muted hover:bg-slate-50'}`}
                          >%</button>
                          <button
                            type="button"
                            onClick={() => {
                              setDiscountMode((m) => ({ ...m, [line._temp_id!]: 'amt' }));
                              handleUpdateLine(line._temp_id, 'discount_pct', 0);
                            }}
                            className={`px-1.5 py-0.5 ${dMode === 'amt' ? 'bg-ink text-white' : 'text-muted hover:bg-slate-50'}`}
                          >Amt</button>
                        </div>
                      </div>
                      {dMode === 'pct' ? (
                        <input
                          type="number"
                          className="input mt-0.5 text-sm"
                          value={line.discount_pct || ''}
                          onChange={(e) => handleUpdateLine(line._temp_id, 'discount_pct', e.target.value === '' ? 0 : Number(e.target.value))}
                          onFocus={(e) => e.target.select()}
                          min={0}
                          max={100}
                          placeholder="0"
                        />
                      ) : (
                        <input
                          type="number"
                          className="input mt-0.5 text-sm"
                          value={line.discount_amount || ''}
                          onChange={(e) => handleUpdateLine(line._temp_id, 'discount_amount', e.target.value === '' ? 0 : Number(e.target.value))}
                          onFocus={(e) => e.target.select()}
                          min={0}
                          placeholder="0"
                        />
                      )}
                      {errors[`line_${idx}_discount`] && (
                        <div className="text-xs text-rdx mt-1">{errors[`line_${idx}_discount`]}</div>
                      )}
                    </div>
                    {/* Line Total */}
                    <div>
                      <label className="label text-xs">Total</label>
                      <div className="h-9 mt-0.5 flex items-center text-sm font-medium text-ink">
                        {(() => {
                          if (previewLine) return formatMoney(previewLine.line_total, currency);
                          const qty = Number(line.quantity) || 0;
                          const price = Number(line.unit_price) || 0;
                          if (!qty || !price) return <span className="text-muted">-</span>;
                          const subtotal = qty * price;
                          const discPct = Number(line.discount_pct) || 0;
                          const discAmt = Number(line.discount_amount) || 0;
                          const total = discPct > 0
                            ? subtotal * (1 - discPct / 100)
                            : subtotal - discAmt;
                          return formatMoney(total, currency);
                        })()}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="col-span-2 lg:col-span-1 flex gap-1 justify-end lg:justify-start pt-1 lg:pt-5 border-t lg:border-t-0 border-line mt-1 lg:mt-0">
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => handleMoveLine(line._temp_id, 'up')}
                        disabled={idx === 0}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => handleMoveLine(line._temp_id, 'down')}
                        disabled={idx === lines.length - 1}
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveLine(line._temp_id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Machine-specific spec fields */}
                  {line._source === 'machine' && selectedConfig && (
                    <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-line">
                      {HEAD_COUNT_FAMILIES.includes(selectedConfig.family) ? (
                        <div className="col-span-2">
                          <label className="label text-xs">Heads</label>
                          {selectedConfig.head_options && selectedConfig.head_options.length > 0 ? (
                            <select
                              className={`input mt-0.5 text-sm ${errors[`line_${idx}_machine`] ? 'input-error' : ''}`}
                              value={String(line.selected_head_count ?? '')}
                              onChange={(e) => handleUpdateLine(line._temp_id, 'selected_head_count', e.target.value === '' ? undefined : Number(e.target.value))}
                            >
                              <option value="">Select…</option>
                              {selectedConfig.head_options.map((h) => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-xs text-rdx mt-0.5">
                              No head-count options configured yet — ask admin to set them in Machine Configs.
                            </div>
                          )}
                          {errors[`line_${idx}_machine`] && (
                            <div className="text-xs text-rdx mt-1">{errors[`line_${idx}_machine`]}</div>
                          )}
                        </div>
                      ) : selectedConfig.family === 'orbiton' ? (
                        <>
                          <div>
                            <label className="label text-xs">Pallets</label>
                            <div className="h-9 mt-0.5 flex items-center text-sm text-muted">
                              {selectedConfig.pallet_count ?? '-'} (derived from colour count)
                            </div>
                          </div>
                          <div>
                            <label className="label text-xs">
                              Flash Heater * {selectedConfig.colour_count != null && (
                                <span className="text-muted font-normal">(max {selectedConfig.colour_count})</span>
                              )}
                            </label>
                            <input
                              type="number"
                              className={`input mt-0.5 text-sm ${errors[`line_${idx}_machine`] ? 'input-error' : ''}`}
                              value={line.flash_heater_value ?? ''}
                              onChange={(e) => handleUpdateLine(line._temp_id, 'flash_heater_value', e.target.value)}
                              min={1}
                              max={selectedConfig.colour_count ?? undefined}
                              placeholder="e.g. 6"
                            />
                            {errors[`line_${idx}_machine`] && (
                              <div className="text-xs text-rdx mt-1">{errors[`line_${idx}_machine`]}</div>
                            )}
                          </div>
                        </>
                      ) : null}
                      {selectedConfig.family === 'roll_format' && selectedConfig.printhead_types && selectedConfig.printhead_types.length > 0 && (
                        <div className="col-span-2">
                          <label className="label text-xs">Printhead Type</label>
                          <select
                            className={`input mt-0.5 text-sm ${errors[`line_${idx}_printhead`] ? 'input-error' : ''}`}
                            value={line.printhead_type ?? ''}
                            onChange={(e) => handleUpdateLine(line._temp_id, 'printhead_type', e.target.value || undefined)}
                          >
                            <option value="">Select…</option>
                            {selectedConfig.printhead_types.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          {errors[`line_${idx}_printhead`] && (
                            <div className="text-xs text-rdx mt-1">{errors[`line_${idx}_printhead`]}</div>
                          )}
                        </div>
                      )}
                      {machineSpecLines(selectedConfig).length > 0 && (
                        <div className="col-span-2">
                          <div className="label text-xs">Spec</div>
                          <ul className="list-disc list-inside text-xs text-muted mt-0.5 space-y-0.5">
                            {machineSpecLines(selectedConfig).map((line, i) => <li key={i}>{line}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <button className="btn btn-secondary w-full gap-2" onClick={handleAddLine}>
              <Plus size={16} /> Add Line Item
            </button>
          </div>
        )}

        {/* Step 2: Tax */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg space-y-3">
              {currency !== 'INR' && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={taxOnExport}
                    onChange={(e) => setTaxOnExport(e.target.checked)}
                  />
                  Apply GST/IGST on this export invoice
                  <span className="text-muted font-normal">
                    (default: export invoices are zero-rated)
                  </span>
                </label>
              )}
              {currency === 'INR' && (
                <div className="max-w-[240px]">
                  <label className="label">Tax Type</label>
                  <select
                    className="input mt-1"
                    value={gstTypeOverride}
                    onChange={(e) => setGstTypeOverride(e.target.value as 'intra' | 'inter')}
                  >
                    <option value="intra">Intra-State (Tamil Nadu)</option>
                    <option value="inter">Inter-State</option>
                  </select>
                  <div className="text-xs text-muted mt-1">
                    Auto-set from the customer's state — override here if needed.
                  </div>
                </div>
              )}
              {(currency === 'INR' || taxOnExport) && (
                <div className="max-w-[200px]">
                  <label className="label">GST Rate (%)</label>
                  <input
                    type="number"
                    className="input mt-1"
                    value={gstRateOverride}
                    onChange={(e) => setGstRateOverride(e.target.value === '' ? '' : Number(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    placeholder="18"
                    min={0}
                    max={100}
                    step={0.01}
                  />
                  <div className="text-xs text-muted mt-1">
                    {gstRateOverride === ''
                      ? "Blank = use each line's own catalog GST rate."
                      : 'Overrides every line on this PI.'}{' '}
                    {currency === 'INR'
                      ? 'Splits into CGST/SGST (Intra-State) or IGST (Inter-State) per the Tax Type above.'
                      : 'Charged as IGST on this export invoice.'}
                  </div>
                </div>
              )}
            </div>

            {previewQuery.isError && (
              <div className="bg-rdx-50 border border-rdx/30 text-rdx-700 text-sm rounded-md p-3">
                <div className="font-semibold mb-1">Couldn't compute tax preview</div>
                <div className="whitespace-pre-line">
                  {(previewQuery.error as any)?.message ?? 'Unknown error'}
                </div>
                <div className="text-xs text-muted mt-2">
                  Go back to <b>Lines</b> and check that every unit price is within the displayed min/max range.
                </div>
                <button
                  className="btn btn-secondary btn-sm mt-2"
                  onClick={() => previewQuery.refetch()}
                >
                  Retry
                </button>
              </div>
            )}
            {previewQuery.isLoading && !previewQuery.isError && (
              <div className="text-center py-12 text-muted">Computing tax preview…</div>
            )}
            {previewQuery.data && !previewQuery.isError ? (
              <div className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="table-clean w-full text-sm">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Subtotal</th>
                        <th>CGST</th>
                        <th>SGST</th>
                        <th>IGST</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewQuery.data.lines.map((l, idx) => {
                        const item = lines[idx] ? getLineCatalogItem(lines[idx]) : undefined;
                        const skuLike = (item as any)?.sku ?? (item as any)?.model_key;
                        return (
                          <tr key={idx}>
                            <td className="text-sm">
                              <div className="font-medium">{item?.name ?? '-'}</div>
                              {skuLike && (
                                <div className="text-xs text-muted">{skuLike}</div>
                              )}
                            </td>
                            <td>{l.quantity}</td>
                            <td>{formatMoney(l.effective_unit_price, currency)}</td>
                            <td>{formatMoney(l.line_subtotal, currency)}</td>
                            <td>{formatMoney(l.cgst_amount, currency)}</td>
                            <td>{formatMoney(l.sgst_amount, currency)}</td>
                            <td>{formatMoney(l.igst_amount, currency)}</td>
                            <td>{formatMoney(l.line_total, currency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-line">
                  <div>
                    <div className="text-xs text-muted">Subtotal</div>
                    <div className="text-lg font-semibold">
                      {formatMoneyWithSymbol(previewQuery.data.subtotal, currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted">Discount</div>
                    <div className="text-lg font-semibold">
                      {formatMoneyWithSymbol(previewQuery.data.discount_total, currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted">Tax</div>
                    <div className="text-lg font-semibold">
                      {formatMoneyWithSymbol(previewQuery.data.tax_total, currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted">Grand Total</div>
                    <div className="text-lg font-semibold text-rdx">
                      {formatMoneyWithSymbol(previewQuery.data.grand_total, currency)}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {!previewQuery.isLoading && !previewQuery.isError && !previewQuery.data && (
              <div className="text-center py-12 text-muted">
                No data yet. Go back to <b>Lines</b> and add at least one line item with a valid price.
              </div>
            )}
          </div>
        )}

        {/* Step 3: Terms */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="label">Warranty Preset</label>
              <SearchableSelect
                className="mt-1"
                value={warrantyPresetId}
                onChange={(val) => {
                  setWarrantyPresetId(val);
                  const preset = (warrantyPresetsQuery.data ?? []).find((w: any) => w.id === val);
                  setWarrantyMonthsOverride(preset ? (preset as any).machine_months : '');
                }}
                placeholder="None"
                clearable
                options={(warrantyPresetsQuery.data ?? []).map((w) => ({
                  value: w.id,
                  label: w.name,
                  sublabel: `${(w as any).machine_months ?? ''} months`,
                }))}
              />
              <div className="mt-2">
                <label className="label text-xs">
                  Warranty (months){' '}
                  <span className="text-muted font-normal">
                    {warrantyPresetId
                      ? '(editable — override if the customer negotiated a different duration)'
                      : '(optional — type a custom duration even without picking a preset above)'}
                  </span>
                </label>
                <input
                  type="number"
                  className="input mt-0.5"
                  value={warrantyMonthsOverride}
                  onChange={(e) => setWarrantyMonthsOverride(e.target.value === '' ? '' : Number(e.target.value))}
                  min={1}
                  placeholder="e.g. 12"
                />
              </div>
            </div>

            <div>
              <label className="label">Lead Time (Weeks)</label>
              <input
                type="number"
                className="input mt-1"
                value={leadTimeWeeks || ''}
                onChange={(e) => setLeadTimeWeeks(e.target.value === '' ? 0 : Number(e.target.value))}
                onFocus={(e) => e.target.select()}
                min={0}
              />
              <div className="text-xs text-muted mt-1">
                Catalog default: {Math.max(...lines.map((l) => getLineCatalogItem(l)?.lead_time_weeks || 0), 0)} weeks (editable — not enforced)
              </div>
            </div>

            <div>
              <label className="label">Delivery Terms (Incoterm)</label>
              <select
                className="input mt-1"
                value={deliveryTerms}
                onChange={(e) => setDeliveryTerms(e.target.value)}
              >
                <option value="">— None —</option>
                <option value="EXW">Ex Works (EXW)</option>
                <option value="FOB">Free On Board (FOB)</option>
                <option value="CIF">Cost, Insurance, and Freight (CIF)</option>
                <option value="CFR">Cost and Freight (CFR)</option>
                <option value="DAP">Delivered at Place (DAP)</option>
                <option value="DDP">Delivered Duty Paid (DDP)</option>
              </select>
              <div className="text-xs text-muted mt-1">
                Optional — when set, adds a "Prices are quoted ..." line to the PDF's Terms &amp; Conditions.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Quote Validity (Days)</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.validityDays ? 'input-error' : ''}`}
                  value={validityDays || ''}
                  onChange={(e) => setValidityDays(e.target.value === '' ? 0 : Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  min={1}
                />
                {errors.validityDays && <div className="text-xs text-rdx mt-1">{errors.validityDays}</div>}
              </div>
              <div>
                <label className="label">Advance (%)</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.advancePct ? 'input-error' : ''}`}
                  value={advancePct}
                  onChange={(e) => setAdvancePct(e.target.value === '' ? '' : Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  min={0}
                  max={100}
                  placeholder="e.g. 30"
                />
                {errors.advancePct && <div className="text-xs text-rdx mt-1">{errors.advancePct}</div>}
              </div>
            </div>

            <div>
              <label className="label">Notes</label>
              <textarea
                className="input mt-1"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
                rows={4}
                placeholder="Additional terms, special instructions..."
              />
              <div className="text-xs text-muted mt-1">{notes.length}/1000 characters</div>
            </div>

            {lines.some((l) => l.discount_pct > 0 || l.discount_amount > 0) && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={showDiscountOnPdf}
                  onChange={(e) => setShowDiscountOnPdf(e.target.checked)}
                />
                <span className="text-sm">Show discount on PDF</span>
              </label>
            )}
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted">Customer</div>
                  <div className="font-medium">{selectedCustomer?.name}</div>
                </div>
                {buyerRefNo && (
                  <div>
                    <div className="text-xs text-muted">Buyer's Ref./Order No.</div>
                    <div className="font-medium">{buyerRefNo}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-muted">Currency</div>
                  <div className="font-medium">{currency}</div>
                </div>
                <div>
                  <div className="text-xs text-muted">PI Date</div>
                  <div className="font-medium">{piDate}</div>
                </div>
                <div>
                  <div className="text-xs text-muted">Validity</div>
                  <div className="font-medium">{validityDays} days</div>
                </div>
                {advancePct !== '' && (
                  <div>
                    <div className="text-xs text-muted">Advance</div>
                    <div className="font-medium">{advancePct}%</div>
                  </div>
                )}
                {deliveryTerms && (
                  <div>
                    <div className="text-xs text-muted">Delivery Terms</div>
                    <div className="font-medium">{deliveryTerms}</div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="font-medium mb-2">Line Items ({lines.length})</div>
              <div className="space-y-2 text-sm">
                {lines.map((l, idx) => {
                  const item = getLineCatalogItem(l);
                  // Same description_snapshot the backend freezes onto the line and
                  // prints on the PDF — so this is exactly what the customer will see,
                  // not a frontend-derived approximation.
                  const snapshot = previewQuery.data?.lines?.[idx]?.description_snapshot ?? '';
                  const dashIdx = snapshot.indexOf(' - ');
                  const specLines = dashIdx >= 0 ? snapshot.slice(dashIdx + 3).split('\n').filter(Boolean) : [];
                  return (
                    <div key={idx}>
                      <div className="flex justify-between text-muted">
                        <span>{item?.name} x {l.quantity}</span>
                        <span>{formatMoneyWithSymbol(Number(l.unit_price) * l.quantity, currency)}</span>
                      </div>
                      {specLines.length > 0 && (
                        <ul className="list-disc list-inside text-xs text-muted mt-0.5 ml-2 space-y-0.5">
                          {specLines.map((sl, i) => <li key={i}>{sl}</li>)}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {previewQuery.data && (
              <div className="bg-slate-50 p-3 rounded-lg space-y-1 text-sm border border-line">
                <div className="flex justify-between">
                  <span className="text-muted">Subtotal</span>
                  <span>{formatMoneyWithSymbol(previewQuery.data.subtotal, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Discount</span>
                  <span className="text-rdx">{formatMoneyWithSymbol(previewQuery.data.discount_total, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Tax</span>
                  <span>{formatMoneyWithSymbol(previewQuery.data.tax_total, currency)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-line font-semibold">
                  <span>Grand Total</span>
                  <span className="text-rdx">{formatMoneyWithSymbol(previewQuery.data.grand_total, currency)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          className="btn btn-secondary gap-1"
          onClick={handlePrev}
          disabled={step === 0}
        >
          <ChevronLeft size={16} /> Back
        </button>

        <div className="flex gap-2">
          {step === STEPS.length - 1 ? (
            <button
              className="btn btn-primary"
              onClick={() => (isEdit ? updateMutation.mutate() : createMutation.mutate())}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          ) : (
            <button
              className="btn btn-primary gap-1"
              onClick={handleNext}
              disabled={step === 1 && previewQuery.isLoading}
            >
              Next <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Inline customer creation modal — opened from Step 1 */}
      <CustomerFormModal
        open={showNewCustomer}
        onClose={() => setShowNewCustomer(false)}
        onSaved={async ({ id }) => {
          // Refetch customers, then auto-select the new one.
          await queryClient.invalidateQueries({ queryKey: ['customers'] });
          setCustomerId(id);
        }}
      />
    </div>
  );
}
